import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { isSupabaseConfigured, supabase } from '../supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; session: Session | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshAdmin = useCallback(async (uid: string | null) => {
    if (!uid || !isSupabaseConfigured) {
      setIsAdmin(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', uid)
        .maybeSingle();
      if (error) {
        console.warn('[auth] admin_users check failed:', error.message);
        setIsAdmin(false);
        return;
      }
      setIsAdmin(Boolean(data));
    } catch (e) {
      console.warn('[auth] admin check error', e);
      setIsAdmin(false);
    }
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          console.warn('[auth] getSession:', error.message);
          setSession(null);
          setUser(null);
          setIsAdmin(false);
        } else {
          const s = data.session;
          setSession(s);
          setUser(s?.user ?? null);
          await refreshAdmin(s?.user?.id ?? null);
        }
      } catch (e) {
        console.warn('[auth] getSession failed', e);
        if (!cancelled) {
          setSession(null);
          setUser(null);
          setIsAdmin(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (cancelled) return;
      setSession(s);
      setUser(s?.user ?? null);
      try {
        await refreshAdmin(s?.user?.id ?? null);
      } catch (e) {
        console.warn('[auth] onAuthStateChange refreshAdmin', e);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [refreshAdmin]);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error('App is not connected to the database. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'), session: null };
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/` : undefined,
      },
    });
    return { error: error ?? null, session: data.session ?? null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error('App is not connected to the database. Check environment variables.') };
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ?? null };
  }, []);

  const signOut = useCallback(async () => {
    if (!isSupabaseConfigured) return;
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('[auth] signOut', e);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    isAdmin,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
